import { defineComponent } from 'vue';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods.js';
import filters from '../../../Shared/filters.js';
import computed from '../../../Shared/computed.js';
import EventBus from '@/Application/event-bus.js';
import TranslationContainer from '@/Application/components/TranslationContainer.jsx';
import careHelpfulFunctions from '../../careHelpfulFunctions.jsx';
import { outlinePrimaryQBtnClassNames } from '../../../Shared/buttonAppearance.js';
import MarkdownIt from 'markdown-it';

import {
    QBtn,
    QCard,
    QCardSection,
    QCardActions,
    QCircularProgress,
    QDialog,
    QExpansionItem,
    QIcon,
    QInput,
    QRadio,
    QSeparator,
    QSpace,
    QToolbar,
    QToolbarTitle,
} from 'quasar';

export default defineComponent({
    name: 'DisplayAskAi',
    emits: ['closeme'],
    components: {
        TranslationContainer,
        QBtn,
        QCard,
        QCardSection,
        QCardActions,
        QCircularProgress,
        QDialog,
        QExpansionItem,
        QIcon,
        QInput,
        QRadio,
        QSeparator,
        QSpace,
        QToolbar,
        QToolbarTitle,
    },
    data: () => ({
        vars: {},
        interactions: [],
        expansionOpen: false,
        useTranscripts: false,
        conversationStarted: false,
        sessionid: '',
        messages: [],
        userInput: '',
        loading: false,
        showExitConfirm: false,
    }),
    props: {
        name: String,
        root: null,
        type: null,
        action: Object,
        context: null,
        scopeitems: null,
        controlscope: null,
    },
    created() {
        if (this.action.ActionData && this.action.ActionData.Vars) {
            const varsexpn = utils.compileObject(this, this.action.ActionData.Vars);
            try {
                this.vars = utils.evaluateObject(varsexpn, this.action.context);
            } catch (e) {
                utils.log('error evaluating Vars: ' + e);
            }
        }

        if (this.action.ActionData && this.action.ActionData.Javascript) {
            try {
                const api = utils.apiWrapper;
                const chf = careHelpfulFunctions;
                this.Javascript = utils.instantiateDynamicControllerScoped(this.action.ActionData.Javascript, { api, contextThis: this, chf }, 'null, null, api, contextThis, chf');

                for (const key in this.Javascript)
                    if (typeof this.Javascript[key] === 'function')
                        this.Javascript[key] = this.Javascript[key].bind(this.Javascript);
            } catch (e) {
                utils.warn(`Failed evaluating Javascript in ${this.name}: ${this.action.ActionData.Javascript}`, e);
            }
        }

        if (this.action.ActionData && this.action.ActionData.InteractionExpression) {
            const interactionExpn = utils.compileExpression(this, this.action.ActionData.InteractionExpression);
            try {
                this.interactions = utils.evaluate(interactionExpn, this.action.context);
            }
            catch (e) {
                utils.log('error evaluating Interactions: ' + e);
            }
        }

        this.$on('Action-Close', this.performClose);
        EventBus.$on(`Action-Close:${this.name}`, this.performClose);
        EventBus.$on(`Action-Close`, this.performClose);

        this.loadInteractions();
    },
    unmounted() {
        this.$off('Action-Close', this.performClose);
        EventBus.$off(`Action-Close:${this.name}`, this.performClose);
        EventBus.$off(`Action-Close`, this.performClose);

        if (this.sessionid) {
            utils.api.request('GET', `/Apps/AI/Insights/AskAI/EndChat?SessionID=${this.sessionid}`);
        }
    },
    computed: {
        ...computed,
        Vars() {
            return this.vars;
        },
        interactionCount() {
            if (!this.interactions) return 0;

            return this.interactions.length;
        },
        callCount() {
            if (!this.interactions) return 0;

            return this.interactions.filter(i => i.ChannelType === 'Call' || i.timelinetype === 'Call').length;
        },
        emailCount() {
            if (!this.interactions) return 0;

            return this.interactions.filter(i => i.ChannelType === 'EMail' || i.timelinetype === 'EMail').length;
        },
        chatCount() {
            if (!this.interactions) return 0;

            return this.interactions.filter(i => i.ChannelType === 'Chat' || i.timelinetype === 'Chat').length;
        },
        otherCount() {
            if (!this.interactions) return 0;

            return this.interactions.filter(i =>
                i.ChannelType !== 'Chat'
                && i.timelinetype !== 'Chat'
                && i.ChannelType !== 'EMail'
                && i.timelinetype !== 'EMail'
                && i.ChannelType !== 'Call'
                && i.timelinetype !== 'Call'
            ).length;
        },
        queues() {
            if (!this.interactions) return [];

            return [...new Set(this.interactions.map(i => i.QueueName || i.queuename).filter(Boolean))];
        },
        analysisDisabled() {
            if (this.interactionCount <= 0)
                return true;

            if (this.useTranscripts) {
                return this.interactionCount > 30;
            } else {
                return this.interactionCount > 100;
            }
        },
        formattedDateRange() {
            if (!this.interactions || this.interactions.length === 0) return null;

            const dates = this.interactions
                .map(i => new Date(i.starttime))
                .filter(d => !isNaN(d));

            if (dates.length === 0) return null;

            const minDate = new Date(Math.min(...dates));
            const maxDate = new Date(Math.max(...dates));

            if (minDate.getTime() === maxDate.getTime()) {
                return minDate.toLocaleString();
            }
            else if (minDate.toDateString() === maxDate.toDateString()) {
                const datePart = minDate.toLocaleDateString();
                const startTime = minDate.toLocaleTimeString();
                const endTime = maxDate.toLocaleTimeString();

                return `${datePart} ${startTime} - ${endTime}`;
            }
            else {
                return `${minDate.toLocaleString()} - ${maxDate.toLocaleString()}`;
            }
        },
    },
    methods: {
        ...methods,
        ...filters,
        async loadInteractions() {
            try {
                if (this.action.ActionData.DataType === 'Raw') {
                    this.interactions = this.action.ActionData.DataRaw || [];
                } else if (this.action.ActionData.DataType === 'URL' && this.action.ActionData.DataURL) {
                    const response = await utils.apiWrapper.request('GET', this.action.ActionData.DataURL);
                    this.interactions = response || [];
                }

                if (this.interactions.length == 1) {
                    this.useTranscripts = true;
                    this.startAnalysis();
                }
            } catch (e) {
                utils.error('Failed to load interactions', e);
            }
        },
        startAnalysis() {
            if (!this.conversationStarted) {
                this.conversationStarted = true;
                this.messages = [{
                    role: 'assistant',
                    content: 'Hi! I am your virtual sidekick and I will help you analyze the interactions you have selected. Where would you like to begin?',
                }];
            }
        },
        async keyUp(e) {
            if (e.key === 'Enter' && !e.shiftKey)
                this.sendMessage();
        },
        async sendMessage() {
            if (!this.userInput.trim()) return;

            var message = this.userInput.trim();
            this.userInput = '';

            this.messages.push({
                role: 'user',
                content: message,
            });
            this.loading = true;

            try {
                var response = await utils.api.request('POST', `/Apps/AI/Insights/AskAI/Ask?SessionID=${this.sessionid}`, {
                    Interactions: this.interactions,
                    UseSummaries: !this.useTranscripts,
                    Message: message,
                });

                if (response.Success) {
                    this.messages.push({
                        role: 'assistant',
                        content: response.Message,
                    });

                    if (response.SessionID && !this.sessionid)
                        this.sessionid = response.SessionID;
                } else {
                    this.messages.push({
                        role: 'assistant',
                        content: 'Sorry, there was an error processing your request.',
                    });
                }
            } catch (e) {
                utils.error('Failed to send message', e);
                this.messages.push({
                    role: 'assistant',
                    content: 'Sorry, there was an error processing your request.',
                });
            } finally {
                this.loading = false;
            }
        },
        handleExit() {
            if (this.conversationStarted && this.messages.length > 1) {
                this.showExitConfirm = true;
            } else {
                this.finish();
            }
        },
        confirmExit() {
            this.showExitConfirm = false;
            this.finish();
        },
        finish() {
            this.$emit('closeme', {
                Success: true,
                DialogResult: {},
                AllowClose: true,
            });
        },
        async export() {
            const lines = ['ID|Channel|Queue|Start Time|Role|Message'];

            for (const i of this.interactions) {
                lines.push(`${i.timelineid || i.sessionid || i.SessionID || ''}|${i.ChannelType || i.channeltype || i.timelinetype || ''}|${i.QueueName || i.queuename || ''}|${i.starttime || ''}||`);
            }

            lines.push('|||||');
            lines.push('|||||');

            for (const m of this.messages) {
                const safeContent = `"${m.content.replace(/"/g, '""')}"`;
                lines.push(`||||${m.role}|${safeContent}`);
            }

            const blob = new Blob([lines.join('\r\n')], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ask-ai-export-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        },
        async performClose(action) {
            this.$emit('closeme', {
                Success: action.ActionData ? action.ActionData.Success : true,
                DialogResult: {},
                AllowClose: true,
            });

            try {
                await utils.success(action);
            } catch (e) { }

            action.FinishFunc(true);
        },
        getMarkdownHtml(text) {
            const md = MarkdownIt();
            return md.render(text);
        },
        getInteractionDetails() {
            const cols = [];

            cols.push(
                <div class="col-auto">
                    <div class="text-caption text-weight-bold" style={{ color: 'rgba(0,0,0,0.6)' }}><TranslationContainer context={this} value="Calls" /></div>
                    <div style={{ fontSize: '14px' }}>{this.callCount}</div>
                </div>,
            );

            if (this.emailCount) {
                cols.push(<QSeparator vertical class="q-mx-sm self-stretch" />);

                cols.push(
                    <div class="col-auto q-pl-md">
                        <div class="text-caption text-weight-bold" style={{ color: 'rgba(0,0,0,0.6)' }}><TranslationContainer context={this} value="Emails" /></div>
                        <div style={{ fontSize: '14px' }}>{this.emailCount}</div>
                    </div>,
                );
            }

            if (this.chatCount) {
                cols.push(<QSeparator vertical class="q-mx-sm self-stretch" />);

                cols.push(
                    <div class="col-auto q-pl-md">
                        <div class="text-caption text-weight-bold" style={{ color: 'rgba(0,0,0,0.6)' }}><TranslationContainer context={this} value="Chats" /></div>
                        <div style={{ fontSize: '14px' }}>{this.chatCount}</div>
                    </div>,
                );
            }

            if (this.otherCount) {
                cols.push(<QSeparator vertical class="q-mx-sm self-stretch" />);

                cols.push(
                    <div class="col-auto q-pl-md">
                        <div class="text-caption text-weight-bold" style={{ color: 'rgba(0,0,0,0.6)' }}><TranslationContainer context={this} value="Other" /></div>
                        <div style={{ fontSize: '14px' }}>{this.otherCount}</div>
                    </div>,
                );
            }

            return (
                <div class="row no-wrap items-start q-mb-md">
                    {cols}
                </div>
            );
        },
    },
    render() {
        const expansionSection = (
            <q-list bordered class="rounded-borders">
                <QExpansionItem
                    class="q-mb-md InteractionSelection"
                    modelValue={this.expansionOpen}
                    onUpdate:modelValue={(v) => { this.expansionOpen = v; }}
                    expandIcon="keyboard_arrow_down"
                >
                    {{
                        header: () => (
                            <div class="col" style={{ display: 'flex', alignItems: 'center', color: 'rgba(0,0,0,0.6)', fontWeight: 700, fontSize: '14px' }}>
                                <TranslationContainer context={this} value="Selected Interactions:" />&nbsp;{this.interactionCount}
                            </div>
                        ),
                        default: () => (
                            <QCardSection>
                                {this.getInteractionDetails()}

                                <div class="q-mb-md">
                                    <div class="text-caption text-weight-bold" style={{ color: 'rgba(0,0,0,0.6)' }}><TranslationContainer context={this} value={`Date & Time ${this.interactionCount == 1 ? '' : 'Range'}`} /></div>
                                    <div style={{ fontSize: '14px' }}>{this.formattedDateRange || 'N/A'}</div>
                                </div>

                                <div>
                                    <div class="text-caption text-weight-bold" style={{ color: 'rgba(0,0,0,0.6)' }}><TranslationContainer context={this} value="Queues" /></div>
                                    <div style={{ fontSize: '14px' }}>{this.queues.join(', ') || 'None'}</div>
                                </div>
                            </QCardSection>
                        ),
                    }}
                </QExpansionItem>
            </q-list>
        );

        let content;

        if (!this.conversationStarted) {
            var errorText = null;
            if (this.analysisDisabled) {
                if (this.interactionCount > 0) {
                    errorText = (
                        <div class="row justify-center q-pa-sm text-negative">
                            <TranslationContainer context={this} value={`Too many interactions for analysis with ${this.useTranscripts ? 'transcripts' : 'summaries'}`} />
                        </div>
                    );
                } else {
                    errorText = (
                        <div class="row justify-center q-pa-sm text-negative">
                            <TranslationContainer context={this} value="No interactions provided for analysis" />
                        </div>
                    );
                }
            }

            content = (
                <QCard class="AskAI column no-wrap" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* L/R inset shared with Silent Monitoring: App.vue `.AskAI > .q-toolbar` → 16px. */}
                    <QToolbar class="bg-white text-dark" style={{ minHeight: '56px', backgroundColor: 'var(--v-white-base)', color: 'var(--v-primaryText-base)' }}>
                        <QToolbarTitle style={{ color: 'var(--v-primaryText-base)', fontSize: '20px', fontWeight: 500 }}>
                            <TranslationContainer context={this} value={this.action.ActionData.Name || 'Ask AI'} />
                        </QToolbarTitle>
                        <QBtn flat dense round onClick={this.finish} aria-label="Close">
                            <QIcon name="mdi-close" />
                        </QBtn>
                    </QToolbar>

                    <QCardSection class="col" style={{ flex: 1, overflowY: 'auto' }}>
                        {expansionSection}

                        <div class="q-mb-lg">
                            <div class="text-subtitle1 q-mb-lg" style={{ color: 'rgba(0,0,0,0.87)' }}><TranslationContainer context={this} value="Choose analysis source" />:</div>

                            <div class="column q-gutter-y-none">
                                <div class="row no-wrap items-start q-mt-none q-mb-md" style={{ height: '150px' }}>
                                    <div class="col" style={{ position: 'relative', height: '100%' }}>
                                        <QRadio
                                            dense
                                            class="AskAiRadioInput items-start"
                                            style={{ alignItems: 'flex-start', margin: '0px' }}
                                            modelValue={this.useTranscripts}
                                            val={false}
                                            onUpdate:modelValue={(v) => { this.useTranscripts = v; }}
                                        >
                                            <div class="q-ml-sm">
                                                <div class="q-mb-xs AskAiRadioInputTitle"><TranslationContainer context={this} value="Summaries" /></div>
                                                <div class="text-grey-7" style={{ fontSize: '14px' }}>
                                                    <TranslationContainer context={this} value="Faster results based on AI-generated interaction summaries." />
                                                </div>
                                                <div class="text-grey-7 q-mb-sm" style={{ fontSize: '14px' }}>
                                                    <TranslationContainer context={this} value="Best for trends and high-level insights." />
                                                </div>
                                            </div>
                                        </QRadio>

                                        <div class="text-positive text-weight-medium" style={{ fontSize: '14px', position: 'absolute', bottom: -20, left: 40 }}>
                                            <TranslationContainer context={this} value="Analyze 100 interactions." />
                                        </div>
                                    </div>

                                    <QSeparator vertical class="q-mx-md self-stretch" />

                                    <div class="col" style={{ position: 'relative', height: '100%' }}>
                                        <QRadio
                                            dense
                                            class="AskAiRadioInput items-start"
                                            style={{ alignItems: 'flex-start', margin: '0px' }}
                                            modelValue={this.useTranscripts}
                                            val={true}
                                            onUpdate:modelValue={(v) => { this.useTranscripts = v; }}
                                        >
                                            <div class="q-ml-sm">
                                                <div class="q-mb-xs AskAiRadioInputTitle"><TranslationContainer context={this} value="Transcripts" /></div>
                                                <div class="text-grey-7" style={{ fontSize: '14px' }}>
                                                    <TranslationContainer context={this} value="More detailed and precise analysis based on full interaction transcripts." />
                                                </div>
                                                <div class="text-grey-7 q-mb-sm" style={{ fontSize: '14px' }}>
                                                    <TranslationContainer context={this} value="Best for deep investigation." />
                                                </div>
                                            </div>
                                        </QRadio>

                                        <div class="text-orange-9 text-weight-medium" style={{ fontSize: '14px', position: 'absolute', bottom: -20, left: 40 }}>
                                            <TranslationContainer context={this} value="Analyze 30 interactions." />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <QBtn color="primary" class="full-width" unelevated onClick={this.startAnalysis} disable={this.analysisDisabled}>
                            <TranslationContainer context={this} value="Start Analysis" />
                        </QBtn>

                        {errorText}
                    </QCardSection>
                </QCard>
            );
        } else {
            content = (
                <QCard class="AskAI column no-wrap" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* L/R inset shared with Silent Monitoring: App.vue `.AskAI > .q-toolbar` → 16px. */}
                    <QToolbar class="bg-white text-dark" style={{ minHeight: '56px', backgroundColor: 'var(--v-white-base)', color: 'var(--v-primaryText-base)' }}>
                        <QToolbarTitle style={{ color: 'var(--v-primaryText-base)', fontSize: '20px', fontWeight: 500 }}>
                            <TranslationContainer context={this} value={this.action.ActionData.Name || 'Ask AI'} />
                        </QToolbarTitle>
                        <QBtn flat dense round onClick={this.handleExit} aria-label="Close">
                            <QIcon name="mdi-close" />
                        </QBtn>
                    </QToolbar>

                    <QCardSection class="col" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {expansionSection}

                        <div class="row items-center justify-between">
                            <div class="text-caption text-weight-medium" style={{ color: 'rgba(0,0,0,0.87)', fontWeight: 500, textAlign: 'center', display: 'flex', alignItems: 'center' }}>
                                <TranslationContainer context={this} value={`Analysis based on interaction ${this.useTranscripts ? 'transcripts' : 'summaries'}`} />
                            </div>

                            <div>
                                <QBtn
                                    outline
                                    color="primary"
                                    no-caps
                                    class={`c-SentenceCaseButton q-ml-sm ${outlinePrimaryQBtnClassNames()}`}
                                    onClick={this.export}
                                >
                                    <TranslationContainer context={this} value="Export" />
                                </QBtn>
                            </div>
                        </div>

                        <div class="chat-messages-container">
                            {this.messages.map((msg, idx) => (
                                <div key={idx} class={`chat-message-wrapper ${msg.role === 'user' ? 'chat-message-user' : 'chat-message-assistant'}`}>
                                    <div class="chat-message-bubble" innerHTML={this.getMarkdownHtml(msg.content)}></div>
                                </div>
                            ))}
                            {this.loading && (
                                <div class="text-center">
                                    <QCircularProgress indeterminate color="primary" size="40px" />
                                </div>
                            )}
                        </div>

                        <QInput
                            class="AskAiInput"
                            modelValue={this.userInput}
                            onUpdate:modelValue={(v) => { this.userInput = v; }}
                            placeholder="Write your message..."
                            type="textarea"
                            outlined
                            dense
                            hideBottomSpace
                            autogrow
                            rows={1}
                            onKeyup={this.keyUp}
                            v-slots={{
                                append: () => (
                                    <QBtn
                                        flat
                                        round
                                        dense
                                        color="primary"
                                        icon="mdi-send"
                                        onClick={this.sendMessage}
                                        disable={!this.userInput.trim() || this.loading}
                                    />
                                ),
                            }}
                        />
                    </QCardSection>

                    <QDialog modelValue={this.showExitConfirm} onUpdate:modelValue={(v) => { this.showExitConfirm = v; }}>
                        <QCard style={{ minWidth: '320px', maxWidth: '400px' }}>
                            <QToolbar class="text-white" style={{ backgroundColor: 'var(--v-navigation-base)', maxHeight: '60px', height: '60px' }}>
                                <QToolbarTitle class="title text-white q-pl-none">
                                    <TranslationContainer context={this} value="End Conversation?" />
                                </QToolbarTitle>

                                <QSpace />

                                <QBtn flat round dense color="white" onClick={() => { this.showExitConfirm = false; }}>
                                    <QIcon name="mdi-close" />
                                </QBtn>
                            </QToolbar>

                            <QCardSection class="q-mt-sm">
                                <TranslationContainer context={this} value="Are you sure you want to end this conversation? These conversations are not saved." />
                            </QCardSection>

                            <QCardActions align="right">
                                <QBtn color="primary" flat unelevated onClick={this.confirmExit}>
                                    <TranslationContainer context={this} value="Confirm" />
                                </QBtn>
                            </QCardActions>
                        </QCard>
                    </QDialog>
                </QCard>
            );
        }

        return content;
    },
});
